import { effectScope, type EffectScope, type ShallowRef } from "vue";
import type { BaseDocumentDto } from "../../types";
import type { MangoQuery } from "../MangoQuery/MangoTypes";
import { HybridQuery, type HybridQueryOptions } from "./HybridQuery";
import type { UseHybridQueryState } from "./useHybridQuery";

/**
 * Process-wide registry of shared {@link HybridQuery} instances, keyed by the query
 * VALUE + every output-affecting option. Lets many call sites that ask for the same
 * reference list (e.g. `{ type: Language }`) share ONE instance — one Dexie live query,
 * one socket listener, one `/query` on a cold start — instead of N independent ones.
 *
 * Reference lists are app-lifetime data, so a shared instance is created lazily and kept
 * for the module lifetime (never ref-counted/disposed) — the same "never disposed" model
 * the app already uses for its global Language query. The owning `scope` is retained only
 * so the test reset can tear it down.
 */
type SharedEntry = { instance: HybridQuery<BaseDocumentDto>; scope: EffectScope };
const _registry = new Map<string, SharedEntry>();

/**
 * Stable structural key over the query VALUE and every output-affecting option. Unlike
 * `structuralCacheKey` (template-based — it extracts values, so it would collide
 * `{ type: language }` with `{ type: group }`), this keeps the values, and folds in the
 * options that change what `output` contains (so two reads that differ only in
 * `stripFields`/`cache`/… correctly do NOT share). The `undefined` sentinel keeps an
 * explicitly-`undefined` field distinct from an absent one.
 */
function sharedKey(query: MangoQuery, options: HybridQueryOptions): string {
    return JSON.stringify(
        {
            q: query,
            live: options.live ?? false,
            cache: options.cache ?? false,
            cacheId: options.cacheId ?? null,
            cacheStripFields: options.cacheStripFields ?? null,
            stripFields: options.stripFields ?? null,
            persistOffline: options.persistOffline ?? false,
            waitForRemote: options.waitForRemote ?? false,
        },
        (_k, v) => (v === undefined ? " undef" : v),
    );
}

function getShared<T extends BaseDocumentDto>(
    query: MangoQuery | (() => MangoQuery),
    options: HybridQueryOptions,
): HybridQuery<T> {
    // Memoization is for CONSTANT reference queries: snapshot the query once (a thunk is
    // evaluated a single time) so the shared instance is built from a static query and the
    // key is stable. A thunk with reactive deps must NOT use the shared composable — its
    // value would be frozen at first acquire and later subscribers would silently get the
    // first caller's instance.
    const q = typeof query === "function" ? query() : query;
    const key = sharedKey(q, options);
    let entry = _registry.get(key);
    if (!entry) {
        let instance!: HybridQuery<BaseDocumentDto>;
        // Build inside a DETACHED scope so the instance is owned by this registry, not by
        // the first subscriber's component scope (whose unmount would otherwise dispose the
        // shared instance out from under every other subscriber). The scope is never stopped
        // in production (reference data lives for the app lifetime).
        const scope = effectScope(true);
        scope.run(() => {
            instance = new HybridQuery<BaseDocumentDto>(q, options);
        });
        entry = { instance, scope };
        _registry.set(key, entry);
    }
    return entry.instance as HybridQuery<T>;
}

/**
 * Like {@link useHybridQueryWithState}, but the underlying {@link HybridQuery} is SHARED
 * across all callers that pass the same query value and options. Use for **constant**
 * reference-list reads (languages, groups, …) so they collapse to one subscription and one
 * cold-start `/query` instead of one per call site. The returned refs are the shared
 * instance's own refs — bind to them read-only.
 *
 * The shared instance is app-lifetime (never disposed), so callers do NOT own its
 * teardown; a component simply reads the refs and its own bindings stop on unmount. Do not
 * pass a reactive thunk (one whose value changes) — see {@link getShared}.
 */
export function useSharedHybridQueryWithState<T extends BaseDocumentDto = BaseDocumentDto>(
    query: MangoQuery | (() => MangoQuery),
    options: HybridQueryOptions = {},
): UseHybridQueryState<T> {
    const q = getShared<T>(query, options);
    return {
        output: q.output,
        isFetching: q.isFetching,
        error: q.error,
        hasLocalChanges: q.hasLocalChanges,
    };
}

/**
 * Shared-instance counterpart to {@link useHybridQuery}: returns only the `output` ref of a
 * {@link HybridQuery} shared across identical (query, options) callers. See
 * {@link useSharedHybridQueryWithState} for the contract and caveats.
 */
export function useSharedHybridQuery<T extends BaseDocumentDto = BaseDocumentDto>(
    query: MangoQuery | (() => MangoQuery),
    options?: HybridQueryOptions,
): ShallowRef<T[]> {
    return useSharedHybridQueryWithState<T>(query, options).output;
}

/** Exposed for diagnostics/tests: how many distinct shared instances are live. */
export function sharedHybridQueryCount(): number {
    return _registry.size;
}

/**
 * @internal Dispose every shared instance and clear the registry. Test-only (guarded), so a
 * leaked shared instance from one test can't bleed into the next via the module-level map.
 */
export function _resetSharedHybridQueryForTests(): void {
    if (import.meta.env?.MODE !== "test") {
        throw new Error("_resetSharedHybridQueryForTests is only available in test mode");
    }
    Array.from(_registry.values()).forEach((e) => e.scope.stop());
    _registry.clear();
}
